import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { CheckCircle, Clock, Shield } from 'lucide-react';
import QRCode from 'react-qr-code';

// URL du tampon O'SCOP
const OSCOP_STAMP_URL = "https://customer-assets.emergentagent.com/job_oscop-platform-1/artifacts/9gq8o1pn_FOGEDOM%20SCIC%20fond%20transparent.png";

// Tampon O'SCOP avec image - DROIT
const OscopStamp = ({ className = "", size = "normal" }) => (
  <div className={`relative ${className}`}>
    <img 
      src={OSCOP_STAMP_URL} 
      alt="Tampon O'SCOP - FOGEDOM SCIC"
      className={size === "large" ? "w-48 h-auto" : "w-36 h-auto"}
      style={{ 
        filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.15))',
        maxWidth: size === "large" ? '200px' : '150px'
      }}
    />
  </div>
);

// Bloc de signature complet
export default function SignatureBlock({ 
  convention, 
  onSign, 
  isSignable = false,
  signatureDestinataire = null
}) {
  const [accepted, setAccepted] = useState(false);
  const [signing, setSigning] = useState(false);

  // Générer URL de vérification
  const getVerificationUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/verify/${convention?.id || 'unknown'}`;
  };

  // Gérer la signature
  const handleSign = async () => {
    if (!accepted) return;
    setSigning(true);
    try {
      if (onSign) {
        await onSign();
      }
    } finally {
      setSigning(false);
    }
  };

  // Date de signature O'SCOP (date d'envoi)
  const dateSignatureOscop = convention?.date_envoi;
  
  // Date de signature destinataire
  const dateSignatureDestinataire = convention?.date_signature || signatureDestinataire?.date;
  
  // Nom du signataire destinataire
  const nomSignataire = signatureDestinataire?.nom || 
    convention?.destinataire_nom || 
    convention?.destinataire_autre?.prenom + ' ' + convention?.destinataire_autre?.nom;

  return (
    <div className="mt-8 space-y-6">
      {/* Séparateur */}
      <div className="border-t-2 border-dashed border-slate-300 pt-6">
        <h3 className="text-lg font-bold text-center text-slate-800 mb-6">
          SIGNATURES
        </h3>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Signature O'SCOP */}
        <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="pt-6">
            <div className="text-center mb-4">
              <Badge className="bg-purple-700 text-white mb-2 font-semibold">O&apos;SCOP - FOGEDOM SCIC</Badge>
              <p className="text-sm text-slate-600">Émetteur de la convention</p>
            </div>

            <div className="flex flex-col items-center gap-4">
              {/* Tampon O'SCOP */}
              <div className="relative py-4">
                <OscopStamp size="large" />
              </div>

              {/* Date de signature */}
              {dateSignatureOscop && (
                <div className="text-center">
                  <p className="text-xs text-slate-500">
                    Signé le {new Date(dateSignatureOscop).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}

              {/* QR Code de vérification si signé */}
              {dateSignatureOscop && (
                <div className="mt-2 p-3 bg-white rounded-lg border border-purple-200">
                  <div className="flex items-center gap-3">
                    <QRCode 
                      value={getVerificationUrl()} 
                      size={60}
                      level="M"
                    />
                    <div className="text-xs text-slate-600">
                      <p className="font-semibold flex items-center gap-1">
                        <Shield className="h-3 w-3 text-green-600" />
                        Signature vérifiée
                      </p>
                      <p className="flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        {new Date(dateSignatureOscop).toLocaleString('fr-FR')}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        Scannez pour vérifier
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Signature Destinataire */}
        <Card className={`border-2 ${dateSignatureDestinataire ? 'border-green-200 bg-gradient-to-br from-green-50 to-white' : 'border-orange-200 bg-gradient-to-br from-orange-50 to-white'}`}>
          <CardContent className="pt-6">
            <div className="text-center mb-4">
              <Badge className={dateSignatureDestinataire ? 'bg-green-700 text-white' : 'bg-orange-700 text-white'}>
                {convention?.type_convention === 'cooper' || convention?.type_contrat === 'cooper' ? "COOPER'S" : 
                 convention?.type_convention === 'expert' || convention?.type_contrat === 'expert' ? "EXPERT" : "Partenaire"}
              </Badge>
              <p className="text-sm text-slate-600 mt-1">
                {nomSignataire || 'Destinataire de la convention'}
              </p>
            </div>

            {dateSignatureDestinataire ? (
              /* Convention signée */
              <div className="flex flex-col items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
                
                <div className="text-center">
                  <p className="font-bold text-green-800">Convention signée</p>
                  <p className="text-sm text-slate-600 mt-1">
                    Par {nomSignataire}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Le {new Date(dateSignatureDestinataire).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>

                {/* QR Code de vérification */}
                <div className="mt-2 p-3 bg-white rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <QRCode 
                      value={`${getVerificationUrl()}?sig=dest`} 
                      size={60}
                      level="M"
                    />
                    <div className="text-xs text-slate-600">
                      <p className="font-semibold flex items-center gap-1">
                        <Shield className="h-3 w-3 text-green-600" />
                        Signature vérifiée
                      </p>
                      <p className="flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        {new Date(dateSignatureDestinataire).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : isSignable ? (
              /* Zone de signature */
              <div className="flex flex-col items-center gap-4">
                <div className="w-full p-4 border-2 border-dashed border-orange-300 rounded-lg bg-white">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="accept" 
                      checked={accepted}
                      onCheckedChange={setAccepted}
                      className="mt-1"
                    />
                    <label htmlFor="accept" className="text-sm text-slate-700 cursor-pointer">
                      J&apos;ai lu et j&apos;approuve l&apos;intégralité des termes de cette convention. 
                      Je reconnais avoir pris connaissance de mes droits et obligations.
                    </label>
                  </div>
                </div>

                <Button
                  onClick={handleSign}
                  disabled={!accepted || signing}
                  className={`w-full ${accepted ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-400'} text-white`}
                >
                  {signing ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      Signature en cours...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Signer la convention
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  En signant, vous acceptez que cette signature électronique 
                  ait la même valeur légale qu&apos;une signature manuscrite.
                </p>
              </div>
            ) : (
              /* En attente de signature */
              <div className="flex flex-col items-center gap-4 py-4 md:py-8">
                <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center">
                  <Clock className="h-12 w-12 text-orange-600" />
                </div>
                <div className="text-center">
                  <p className="font-bold text-orange-800">En attente de signature</p>
                  <p className="text-sm text-slate-600 mt-1">
                    La convention a été envoyée et attend la signature du destinataire.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Mentions légales */}
      <div className="text-xs text-slate-500 text-center mt-4 p-3 bg-slate-50 rounded-lg">
        <p>
          Cette convention est établie conformément aux dispositions du Code civil français.
          Les signatures électroniques apposées ont valeur légale selon le règlement eIDAS.
        </p>
        <p className="mt-1">
          Référence: {convention?.numero_contrat} • 
          Émis le: {convention?.created_at ? new Date(convention.created_at).toLocaleDateString('fr-FR') : '-'}
        </p>
      </div>
    </div>
  );
}

// Export du composant tampon
export { OscopStamp };
